import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import enCommon from "./locales/en/common.json";
import urCommon from "./locales/ur/common.json";

const LANG_KEY = "lang";
const DEFAULT_LANGUAGE = "en";
const SUPPORTED_LANGUAGES = ["en", "ur"];

/** Map region codes (e.g. ur-PK) to supported base codes so `dir` and resources stay in sync. */
const resolveBaseLanguage = (language) => {
  if (language == null || language === "") return DEFAULT_LANGUAGE;
  const s = String(language);
  if (SUPPORTED_LANGUAGES.includes(s)) return s;
  const base = s.split(/[-_]/)[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : DEFAULT_LANGUAGE;
};

const normalizeLanguage = (language) => resolveBaseLanguage(language);

/** Text direction for `document.documentElement` (Urdu = rtl). MUI shell uses `theme.direction: ltr`; Urdu form fields use `FormDirectionScope`. */
export function getTextDirection(language) {
  return normalizeLanguage(language) === "ur" ? "rtl" : "ltr";
}

/**
 * Bootstrap margin for an icon placed before its label: `me-2` (LTR) vs `ms-2` (Urdu/RTL) so
 * the gap between icon and text stays on the text side in both directions.
 */
export function iconBeforeLabelClass(language) {
  return getTextDirection(language) === "rtl" ? "ms-2" : "me-2";
}

const applyDocumentLanguage = (language) => {
  if (typeof document === "undefined") return;

  const normalizedLanguage = normalizeLanguage(language);
  const root = document.documentElement;
  const body = document.body;

  root.lang = normalizedLanguage;
  root.dir = getTextDirection(normalizedLanguage);

  if (body) {
    body.classList.remove("lang-en", "lang-ur");
    body.classList.add(`lang-${normalizedLanguage}`);
  }
};

const initialLanguage = normalizeLanguage(
  typeof window !== "undefined"
    ? window.localStorage.getItem(LANG_KEY) || DEFAULT_LANGUAGE
    : DEFAULT_LANGUAGE
);

i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon },
    ur: { common: urCommon },
  },
  lng: initialLanguage,
  fallbackLng: "en",
  supportedLngs: SUPPORTED_LANGUAGES,
  nonExplicitSupportedLngs: true,
  load: "languageOnly",
  defaultNS: "common",
  ns: ["common"],
  interpolation: {
    escapeValue: false,
  },
});

applyDocumentLanguage(i18n.language);

i18n.on("languageChanged", (language) => {
  const normalizedLanguage = normalizeLanguage(language);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LANG_KEY, normalizedLanguage);
  }
  applyDocumentLanguage(normalizedLanguage);
});

export { LANG_KEY, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, resolveBaseLanguage };
export default i18n;
